import { useEffect, useState } from "react";
import { _post, _get } from "../services/apiClient";
import { apiUrls } from "../config/apiUrls.const";
import { showToast } from "../services/alertService";
import { getSecureItem, setStoredInfo, getStoredInfo } from "../utils/LocalStorageManager";
import { Col, Form, Row } from "react-bootstrap";
import { useUser } from "../contexts/UserContext";
import axios from "axios";

interface LocationProps {
    onSuccess: (title: string) => void;
}

export default function Location({ onSuccess }: LocationProps) {
    const [divisions, setDivisions] = useState<any[]>([]);
    const [districts, setDistricts] = useState<any[]>([]);
    const [thanas, setThanas] = useState<any[]>([]);
    const [data, setData] = useState<any[]>([]);

    const [divisionId, setDivisionId] = useState<any>("");
    const [districtId, setDistrictId] = useState<any>("");
    const [thanaId, setThanaId] = useState<any>("");

    const [activeDivision, setActiveDivision] = useState<any>();
    const [activeDistrict, setActiveDistrict] = useState<any>();
    const [activeThana, setActiveThana] = useState<any>();

    const { user } = useUser();
    const isAuthenticated = !!getSecureItem("access_token") && !!user;
    const [submitted, setSubmitted] = useState(false);
    const [activeLocation, setActiveLocation] = useState<any>(getStoredInfo('location'));
    const [storedLocations, setStoredLocations] = useState<any[]>([]);

    // Load Location Data
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await axios.get(apiUrls.locations, {
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
                const locationData = res.data;
                setData(locationData);
                setDivisions(
                    locationData.map((d: any) => ({
                        id: d.id,
                        label: d.name_en
                    }))
                );
            } catch (error) {
                console.error("Location API error:", error);
            }
        };

        fetchLocations();
        if (isAuthenticated && user) {
            getPreviousLocations();
        }
    }, []);

    async function getPreviousLocations() {
        try {
            const resp = _get(apiUrls.getStoredLocation);

            if ((await resp)?.data?.success) {
                setStoredLocations((await resp).data.data);
            }
        } catch (err) {
            // ignore silently
        }
    }

    // Pre-set saved location AFTER data is ready
    useEffect(() => {
        if (!activeLocation || data.length === 0) return;
        applyActiveLocation(activeLocation);
    }, [data, activeLocation]);

    function applyActiveLocation(selectedLocation: any) {
        if (!selectedLocation || data.length === 0) return;

        const div = data.find((d: any) => d.id == selectedLocation.division.id);
        if (div) {
            setDivisionId(div.id);
            setDistricts(div.districts || []);
            setActiveDivision(div);
        }

        const dist = div?.districts.find((d: any) => d.id == selectedLocation.district.id);
        if (dist) {
            setDistrictId(dist.id);
            setThanas(dist.thanas || []);
            setActiveDistrict(dist);
        }

        const thana = dist?.thanas.find((t: any) => t.id == selectedLocation.thana.id);
        if (thana) {
            setThanaId(thana.id);
            setActiveThana(thana);
        }
    }

    function updateDivisionInfo(id: any) {
        setDivisionId(id);
        setDistrictId("");
        setThanaId("");
        setDistricts([]);
        setThanas([]);

        const division = data.find((d: any) => d.id == id);
        if (division) {
            setDistricts(division.districts || []);
            setActiveDivision(division);
        };
    }

    function updateDistrictInfo(id: any) {
        setDistrictId(id);
        setThanaId("");
        setThanas([]);

        const district = districts.find((d: any) => d.id == id);
        if (district) {
            setActiveDistrict(district);
            setThanas(district.thanas || []);
        }
    }

    function updateThanaInfo(id: any) {
        setThanaId(id);

        const thana = thanas.find((t: any) => t.id == id);
        if (thana) {
            setActiveThana(thana);
        }
    }

    function validateFields() {
        const missing = [];
        if (!divisionId) missing.push("Division");
        if (!districtId) missing.push("District");
        // if (!thanaId) missing.push("Thana");

        if (missing.length > 0) {
            showToast({
                icon: "error",
                title: "Required Fields Missing",
                text: "Please select: " + missing.join(", "),
            });
            return false;
        }
        return true;
    }

    async function saveToDatabase() {
        try {
            const res: any = await _post(apiUrls.saveLocation, {
                division: { id: divisionId },
                district: { id: districtId },
                thana: { id: thanaId },
            });

            if (res?.data?.success) {
                const selectedData = {
                    division: {
                        id: activeDivision?.id,
                        name_en: activeDivision?.name_en,
                        name_bn: activeDivision?.name_bn
                    },
                    district: {
                        id: activeDistrict?.id,
                        name_en: activeDistrict?.name_en,
                        name_bn: activeDistrict?.name_bn
                    },
                    thana: {
                        id: activeThana?.id,
                        name_en: activeThana?.name_en,
                        name_bn: activeThana?.name_bn
                    }
                };
                setStoredInfo("location", selectedData);
                showToast({ icon: "success", title: "Location Saved Successfully" });
            } else {
                showToast({ icon: "error", title: "Unable to save location" });
            }
        } catch (err) {
            showToast({ icon: "error", title: "Network Error while saving" });
        }
    }


    function selectLocation(save: boolean) {
        setSubmitted(true);
        if (!validateFields()) return;

        const selectedData = {
            division: {
                id: activeDivision?.id,
                name_en: activeDivision?.name_en,
                name_bn: activeDivision?.name_bn
            },
            district: {
                id: activeDistrict?.id,
                name_en: activeDistrict?.name_en,
                name_bn: activeDistrict?.name_bn
            },
            thana: {
                id: activeThana?.id,
                name_en: activeThana?.name_en,
                name_bn: activeThana?.name_bn
            }
        };
        setStoredInfo("location", selectedData);

        if (save) {
            saveToDatabase();
        }

        if (selectedData?.thana?.name_en || selectedData?.district?.name_en || selectedData?.division?.name_en) {
            onSuccess(selectedData?.thana?.name_en || selectedData?.district?.name_en || selectedData?.division?.name_en || '');
        }
    }

    function deleteStoredLocation(id: number) {
        async function deleteLocation() {
            try {
                const deleteAction: any = await _post(apiUrls.deleteLocation, {
                    stored_location_id: id
                });
                if (deleteAction?.data?.success) {
                    getPreviousLocations();
                }
            } catch (err) {
                showToast({ icon: "error", title: "Network Error while saving" });
            }
        }
        deleteLocation();

    }

    function activateStoredLocation(id: number) {
        async function activateLocation() {
            const getLocation = storedLocations.map((loc: any) => {
                if (loc?.id == id) {
                    // return loc;
                    const selectedData = {
                        division: {
                            id: loc?.division?.id,
                            name_en: loc?.division?.name_en,
                            name_bn: loc?.division?.name_bn,
                        },
                        district: {
                            id: loc?.district?.id,
                            name_en: loc?.district?.name_en,
                            name_bn: loc?.district?.name_bn,
                        },
                        thana: {
                            id: loc?.thana?.id,
                            name_en: loc?.thana?.name_en,
                            name_bn: loc?.thana?.name_bn,
                        }
                    };
                    setStoredInfo("location", selectedData);
                    setActiveLocation(selectedData)
                    applyActiveLocation(selectedData);
                }
            })
            try {
                const activateAction: any = await _post(apiUrls.activateLocation, {
                    stored_location_id: id
                });
                if (activateAction?.data?.success) {
                    getPreviousLocations();
                }
            } catch (err) {
                showToast({ icon: "error", title: "Network Error while saving" });
            }
        }
        activateLocation();
    }

    return (
        <>
            <hr className="mb-2" />
            {/*  */}
            <Row className="">
                <div className="col-12 mb-2 text-center">
                    <b><small>Previously Stored Locations</small></b>
                </div>
                {isAuthenticated && storedLocations?.length > 0 && (
                    <>
                        {storedLocations.map((d) => {
                            const title = [
                                d?.division?.name_en,
                                d?.district?.name_en,
                                d?.thana?.name_en
                            ].filter(Boolean).join(",  ");

                            return (
                                <div key={d.id} className="col-12 d-flex justify-content-center mb-1">
                                    <small className="me-2 mt-1">{title}</small>
                                    <div className="ms-2">
                                        {d?.active && (
                                            <span className="me-2">
                                                <i className="bi bi-check-square-fill text-primary"></i>
                                            </span>
                                        )}
                                        {!d?.active && (
                                            <span className="me-2 pointer" onClick={() => activateStoredLocation(d.id)}>
                                                <i className="bi bi-square text-primary"></i>
                                            </span>
                                        )}
                                        <span className="pointer" onClick={() => deleteStoredLocation(d.id)}>
                                            <i className="bi bi-trash-fill text-danger"></i>
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </Row>

            <hr />
            <Row className="mt-2">
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Division</Form.Label>
                        <Form.Select value={divisionId} onChange={(e) => updateDivisionInfo(e.target.value)}>
                            <option value="">Select Division</option>
                            {divisions.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.label}
                                </option>
                            ))}
                        </Form.Select>
                        {submitted && !divisionId && <small className="text-danger">Division is required!</small>}
                    </Form.Group>
                </Col>

                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>District</Form.Label>
                        <Form.Select value={districtId} onChange={(e) => updateDistrictInfo(e.target.value)}>
                            <option value="">Select District</option>
                            {districts.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name_en}
                                </option>
                            ))}
                        </Form.Select>
                        {submitted && !districtId && <small className="text-danger">District is required!</small>}
                    </Form.Group>
                </Col>

                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Thana</Form.Label>
                        <Form.Select value={thanaId} onChange={(e) => updateThanaInfo(e.target.value)}>
                            <option value="">Select Thana</option>
                            {thanas.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name_en}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>

                <Col md={12} className="text-end">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => selectLocation(false)}>
                        Select Location
                    </button>
                    {isAuthenticated && (
                        <button className="btn btn-sm btn-outline-success ms-2" onClick={() => selectLocation(true)}>
                            Select & Save Location
                        </button>
                    )}
                </Col>
            </Row>
        </>
    );
}
